import {
  createBrowserRouter,
  RouterProvider,
  redirect,
} from "react-router-dom";

import AppLayout from "@layouts/Layout";
import { RequireAuth } from "@components/ProtectRouting";
import { Unauthorized } from "@components/Unauthorized";

import { ScrollProvider } from "@contexts/scroll";
import { NotFound } from "@components/NotFound";
import { lazyNamed } from "@utils/functions";
import { Suspense } from "react";
import { GenericLoader } from "@components/Loader";


const DashboardPage = lazyNamed(() => import("@features/dashboard/pages"), "DashboardPage");

const AdminProfilePage = lazyNamed(() => import("@features/users/pages"), "AdminProfilePage");
const UserListPage = lazyNamed(() => import("@features/users/pages"), "UserListPage");
const UserDetailsPage = lazyNamed(() => import("@features/users/pages"), "UserDetailsPage");

const CompanyListPage = lazyNamed(() => import("@features/company/pages"), "CompanyListPage");
const CompanyDetailsPage = lazyNamed(() => import("@features/company/pages"), "CompanyDetailsPage");

const MachineryListPage = lazyNamed(() => import("@features/machinery/pages"), "MachineryListPage");
const MachineryDetailsPage = lazyNamed(() => import("@features/machinery/pages"), "MachineryDetailsPage");
const MachineryRequestListPage = lazyNamed(() => import("@features/machinery/pages"), "MachineryRequestListPage");

const MachineryCategoryListPage = lazyNamed(() => import("@features/machinery-category/pages"), "MachineryCategoryListPage");
const MachineryCategoryFormPage = lazyNamed(() => import("@features/machinery-category/pages"), "MachineryCategoryFormPage");
const MachinerySubCategoryFormPage = lazyNamed(() => import("@features/machinery-category/pages"), "MachinerySubCategoryFormPage");
const MachinerysubCategoryListPage = lazyNamed(() => import("@features/machinery-category/pages"), "MachinerysubCategoryListPage");

const AuctionListPage = lazyNamed(() => import("@features/auction/pages"), "AuctionListPage");
const AuctionDetailsPage = lazyNamed(() => import("@features/auction/pages"), "AuctionDetailsPage");
const AuctionEditFormPage = lazyNamed(() => import("@features/auction/pages"), "AuctionEditFormPage");
const BidListPage = lazyNamed(() => import("@features/auction/pages"), "BidListPage");

const InvoiceListPage = lazyNamed(() => import("@features/invoice/pages"), "InvoiceListPage");
const InvoiceDetailsPage = lazyNamed(() => import("@features/invoice/pages"), "InvoiceDetailsPage");

const AuthPage = lazyNamed(() => import("@features/auth/pages"), "AuthPage");
const ChangePasswordFormPage = lazyNamed(() => import("@features/auth/pages"), "ChangePasswordFormPage");

const PlansListPage = lazyNamed(() => import("@features/subscription-plan/pages"), "PlansListPage");
const CreatePlansFormPage = lazyNamed(() => import("@features/subscription-plan/pages"), "CreatePlansFormPage");

const NotificationListPage = lazyNamed(() => import("@features/notification/pages"), "NotificationListPage");

const PaymentListPage = lazyNamed(() => import("@features/payment/pages"), "PaymentListPage");

const NewsLetterSubscriberListPage = lazyNamed(() => import("@features/newsletter/pages"), "NewsLetterSubscriberListPage");

const NewsListPage = lazyNamed(() => import("@features/news/pages"), "NewsListPage");
const NewCategoryListPage = lazyNamed(() => import("@features/news/pages"), "NewCategoryListPage");
const NewsFormPage = lazyNamed(() => import("@features/news/pages"), "NewsFormPage");
const NewsTagListPage = lazyNamed(() => import("@features/news/pages"), "NewsTagListPage");

const SupportQueryListPage = lazyNamed(() => import("@features/support/pages"), "SupportQueryListPage");

const PolicyPage = lazyNamed(() => import("@features/policy/pages"), "PolicyPage");

const BrandListingPage = lazyNamed(() => import("@features/brand/pages"), "BrandListingPage");
const BrandFormPage = lazyNamed(() => import("@features/brand/pages"), "BrandFormPage");

const FaqListPage = lazyNamed(() => import("@features/faq/pages"), "FaqListPage");
const FaqFormPage = lazyNamed(() => import("@features/faq/pages"), "FaqFormPage");

const BlogListPage = lazyNamed(() => import("@features/blog/pages"), "BlogListPage");
const BlogCategoryListPage = lazyNamed(() => import("@features/blog/pages"), "BlogCategoryListPage");
const BlogCategoryForm = lazyNamed(() => import("@features/blog/pages"), "BlogCategoryForm");
const BlogFormPage = lazyNamed(() => import("@features/blog/pages"), "BlogFormPage");

const router = createBrowserRouter([
  {
    path: "/",
    loader: () => redirect("/admin/dashboard"), // Redirect "/" to "/admin/dashboard"
  },
  {
    path: "/admin",
    element: (
      <RequireAuth allowedRoles={["admin"]}>
        <ScrollProvider>
          <AppLayout />
        </ScrollProvider>

      </RequireAuth>
    ),
    children: [
      {
        path: "dashboard",
        element: <DashboardPage />,
      },
      {
        path: "profile",
        element: <AdminProfilePage />,
      },

      {
        path: "machineries",
        element: <MachineryListPage />,
      },
      {
        path: "machinery-details/:id",
        element: <MachineryDetailsPage />,
      },
      {
        path: "machinery-update-requests",
        element: <MachineryRequestListPage />,
      },

      {
        path: "users",
        element: <UserListPage />,
      },
      {
        path: "user/:id",
        element: <UserDetailsPage />,
      },
      {
        path: "companies",
        element: <CompanyListPage />,
      },
      {
        path: "companies/:id",
        element: <CompanyDetailsPage />,
      },
      {
        path: "payments",
        element: <PaymentListPage />,
      },
      {
        path: "auctions-request",
        element: <AuctionListPage />,
      },
      {
        path: "auction-details/:id",
        element: <AuctionDetailsPage />,
      },
      {
        path: "edit-auction/:id",
        element: <AuctionEditFormPage />,
      },
      {
        path: "bid-list/:auctionId",
        element: <BidListPage />
      },
      {
        path: "plans",
        element: <PlansListPage />,
      },

      {
        path: "plans/create-plan",
        element: <CreatePlansFormPage />,
      },
      {
        path: "plans/edit-plan/:planId",
        element: <CreatePlansFormPage />,
      },
      {
        path: "invoices",
        element: <InvoiceListPage />,
      },
      {
        path: "invoices/invoice-details/:invoiceId",
        element: <InvoiceDetailsPage />,
      },
      {
        path: "policies",
        element: <PolicyPage />,
      },
      {
        path: "blog",
        element: <BlogListPage />,
      },
      {
        path: "blog/create",
        element: <BlogFormPage />,
      },
      {
        path: "blog/:blogId",
        element: <BlogFormPage />,

      },
      {
        path: "categories",
        element: <BlogCategoryListPage />,
      },
      {
        path: "categories/create",
        element: <BlogCategoryForm />,
      },
      {
        path: "categories/:categoryId",
        element: <BlogCategoryForm />,
      },
      {
        path: "news/all",
        element: <NewsListPage />,
      },
      {
        path: "news/create",
        element: <NewsFormPage />,
      },
      {
        path: "news/:newsId",
        element: <NewsFormPage />,
      },
      {
        path: "news/categories",
        element: <NewCategoryListPage />,
      },
      {
        path: "news/tags",
        element: <NewsTagListPage />,
      },
      {
        path: "notifications",
        element: <NotificationListPage />,
      },
      {
        path: "change-password",
        element: <ChangePasswordFormPage />,
      },
      // Brands
      {
        path: "brand-list",
        element: <BrandListingPage />,
      },
      {
        path: "brand-create-edit/:id?",
        element: <BrandFormPage />,
      },
      // Machinery Category List
      // {
      //   path: "machinery-categories-list",
      //   element: <CategoriesList />,
      // },

      {
        path: "machinery-categories-list",
        element: <MachineryCategoryListPage />,
      },
      // {
      //   path: "machinery-categories-create-edit/:id?",
      //   element: <UploadProvider><CategoryForm /></UploadProvider>,
      // },
      // {
      //   path: "machinery-category-view/:id",
      //   element: <UploadProvider><CategoryForm /></UploadProvider>,

      // },
      {
        path: "machinery-categories-create-edit/:id?",
        element: <MachineryCategoryFormPage />,
      },
      {
        path: "machinery-category-view/:id",
        element: <MachineryCategoryFormPage />,

      },
      // Machinery Sub categories
      {
        path: "machinery-sub-categories-list",
        element: < MachinerysubCategoryListPage />,
      },

      //**  */
      {
        path: "subcategory-create-edit/:id?",
        element: <MachinerySubCategoryFormPage />,
      },
      {
        path: "subcategory-view/:id?",
        element: <MachinerySubCategoryFormPage />,

      },
      {
        path: "subscribers-list",
        element: <NewsLetterSubscriberListPage />,
      },

      //** FAQ */

      {
        path: "faq-list",
        element: <FaqListPage />,
      },
      {
        path: "faqs-create-edit/:id?",
        element: <FaqFormPage />,
      },
      {
        path: "faqs-view/:id?",
        element: <FaqFormPage />,
      },
      {
        path: "support",
        element: <SupportQueryListPage />,
      },
    ],
  },
  {
    path: "/login",
    element: <AuthPage />,
  },
  {
    path: "/signup",
    element: <AuthPage />,
  },
  {
    path: "/forgot-password",
    element: <AuthPage />,
  },
  {
    path: "/reset-password",
    element: <AuthPage />,
  },
  {
    path: "/become-partner",
    element: <AuthPage />,
  },
  {
    path: "/unauthorized",
    element: <Unauthorized />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
]);

const App = () => {

  return (
    <Suspense fallback={<GenericLoader />}>
      <RouterProvider router={router} />
    </Suspense>
  );
};
export default App;
